import { type ReactNode, useCallback, useMemo, useState } from 'react';
import type { HunkData, ViewType } from 'react-diff-view';
import { Diff, Hunk, parseDiff, tokenize } from 'react-diff-view';
import { refractor } from 'refractor/lib/all.js';

import 'react-diff-view/style/index.css';
import './diff-viewer.css';

/**
 * Adapter for refractor v4 to work with react-diff-view's tokenize.
 * Refractor v4 returns a HAST root node { type: "root", children: [...] },
 * but react-diff-view expects highlight() to return the children array directly.
 */
const refractorAdapter = {
  highlight: (code: string, language: string) => refractor.highlight(code, language).children,
};

/** Represents a single file diff passed to the DiffViewer */
export interface FileDiffData {
  /** Path to the file */
  filePath: string;
  /** Type of change */
  changeType: 'ADDED' | 'MODIFIED' | 'DELETED' | 'RENAMED';
  /** Old file path (for renames) */
  oldFilePath?: string;
  /** Unified diff text for this file */
  diffText?: string;
  /** Whether the file is binary */
  isBinary?: boolean;
  /** Number of added lines */
  additions: number;
  /** Number of deleted lines */
  deletions: number;
}

export interface DiffViewerProps {
  /** Array of file diffs to display */
  files: FileDiffData[];
  /** View mode: unified or split */
  viewMode: 'unified' | 'split';
  /** Whether the diff data is currently loading */
  isLoading?: boolean;
  /** Callback when a gutter line is clicked (for inline comment placement) */
  onGutterClick?: (filePath: string, lineNumber: number, side: 'LEFT' | 'RIGHT') => void;
  /** Callback when a file header is clicked */
  onFileSelect?: (filePath: string) => void;
  /** Widget to render inline below a specific file (e.g., comment form) */
  inlineCommentWidget?: { filePath: string; element: ReactNode };
}

/** Threshold for collapsing large diffs */
export const LARGE_DIFF_THRESHOLD = 500;

/** Maps our change types to react-diff-view's DiffType */
function mapChangeType(changeType: FileDiffData['changeType']): 'add' | 'delete' | 'modify' | 'rename' {
  const map: Record<string, 'add' | 'delete' | 'modify' | 'rename'> = {
    ADDED: 'add',
    MODIFIED: 'modify',
    DELETED: 'delete',
    RENAMED: 'rename',
  };
  return map[changeType] ?? 'modify';
}

/** Maps file extensions to refractor language identifiers */
function getLanguageFromPath(filePath: string): string | undefined {
  const ext = filePath.split('.').pop()?.toLowerCase();
  if (!ext) return undefined;

  const extMap: Record<string, string> = {
    ts: 'typescript',
    tsx: 'tsx',
    js: 'javascript',
    jsx: 'jsx',
    json: 'json',
    md: 'markdown',
    css: 'css',
    scss: 'scss',
    less: 'less',
    html: 'markup',
    xml: 'markup',
    svg: 'markup',
    yml: 'yaml',
    yaml: 'yaml',
    sh: 'bash',
    bash: 'bash',
    zsh: 'bash',
    py: 'python',
    rb: 'ruby',
    java: 'java',
    kt: 'kotlin',
    kts: 'kotlin',
    go: 'go',
    rs: 'rust',
    c: 'c',
    h: 'c',
    cpp: 'cpp',
    hpp: 'cpp',
    cs: 'csharp',
    sql: 'sql',
    graphql: 'graphql',
    gql: 'graphql',
    dockerfile: 'docker',
    tf: 'hcl',
    toml: 'toml',
    ini: 'ini',
    php: 'php',
    swift: 'swift',
    scala: 'scala',
    r: 'r',
    lua: 'lua',
    vim: 'vim',
    diff: 'diff',
    makefile: 'makefile',
    gradle: 'groovy',
  };

  return extMap[ext];
}

/** Tokenizes hunks for syntax highlighting */
function useTokenizedHunks(hunks: HunkData[], filePath: string, viewMode: 'unified' | 'split') {
  return useMemo(() => {
    if (hunks.length === 0) return undefined;

    const language = getLanguageFromPath(filePath);
    if (!language) return undefined;

    // Check if refractor supports this language
    if (!refractor.registered(language)) return undefined;

    try {
      return tokenize(hunks, {
        highlight: true,
        refractor: refractorAdapter,
        language,
      });
    } catch {
      // If tokenization fails, return undefined to fall back to plain text
      return undefined;
    }
  }, [hunks, filePath, viewMode]);
}

/** Calculates total changed lines for a file */
export function getTotalChangedLines(file: FileDiffData): number {
  return file.additions + file.deletions;
}

/** Determines if a file should be collapsed by default */
export function shouldCollapseByDefault(file: FileDiffData): boolean {
  return getTotalChangedLines(file) > LARGE_DIFF_THRESHOLD;
}

/** Determines if a file is binary */
export function isBinaryFile(file: FileDiffData): boolean {
  return file.isBinary === true;
}

/** Loading skeleton for the diff viewer */
function DiffSkeleton() {
  return (
    <div
      className="animate-pulse space-y-4"
      role="status"
      aria-label="Loading diff"
    >
      {[1, 2, 3].map((i) => (
        <div key={i} className="rounded-lg border border-border">
          {/* File header skeleton */}
          <div className="flex items-center gap-3 border-b border-border bg-surface-raised px-4 py-3">
            <div className="h-4 w-48 rounded bg-gray-200 dark:bg-gray-600" />
            <div className="h-4 w-20 rounded bg-gray-200 dark:bg-gray-600" />
          </div>
          {/* Diff lines skeleton */}
          <div className="space-y-1 p-4">
            {[1, 2, 3, 4, 5, 6].map((j) => (
              <div key={j} className="flex items-center gap-2">
                <div className="h-4 w-8 rounded bg-gray-100 dark:bg-gray-700" />
                <div className="h-4 w-8 rounded bg-gray-100 dark:bg-gray-700" />
                <div
                  className="h-4 rounded bg-gray-100 dark:bg-gray-700"
                  style={{ width: `${40 + Math.random() * 40}%` }}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Header for each file in the diff */
function FileHeader({
  file,
  isCollapsed,
  onToggleCollapse,
}: {
  file: FileDiffData;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}) {
  return (
    <div className="flex items-center gap-3 border-b border-border bg-surface-raised px-4 py-2.5">
      <button
        onClick={onToggleCollapse}
        className="flex-shrink-0 rounded p-0.5 text-content-tertiary hover:bg-surface-overlay hover:text-content focus:outline-none focus:ring-2 focus:ring-brand-600"
        aria-expanded={!isCollapsed}
        aria-label={isCollapsed ? 'Expand file diff' : 'Collapse file diff'}
      >
        <svg
          className={`h-4 w-4 transition-transform ${isCollapsed ? '' : 'rotate-90'}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>

      <ChangeTypeBadge changeType={file.changeType} />

      <span className="min-w-0 flex-1 truncate font-mono text-sm text-content">
        {file.filePath}
        {file.changeType === 'RENAMED' && file.oldFilePath && (
          <span className="text-content-tertiary">
            {' '}← {file.oldFilePath}
          </span>
        )}
      </span>

      <span className="flex-shrink-0 text-xs">
        <span className="text-success">+{file.additions}</span>
        {' / '}
        <span className="text-danger">-{file.deletions}</span>
      </span>
    </div>
  );
}

/** Badge showing the change type */
function ChangeTypeBadge({ changeType }: { changeType: FileDiffData['changeType'] }) {
  const config: Record<string, { label: string; className: string }> = {
    ADDED: {
      label: 'Added',
      className: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
    },
    MODIFIED: {
      label: 'Modified',
      className: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
    },
    DELETED: {
      label: 'Deleted',
      className: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
    },
    RENAMED: {
      label: 'Renamed',
      className: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
    },
  };

  const { label, className } = config[changeType] ?? {
    label: 'Unknown',
    className: 'bg-surface-raised text-content-secondary',
  };

  return (
    <span className={`inline-flex flex-shrink-0 rounded px-1.5 py-0.5 text-xs font-medium ${className}`}>
      {label}
    </span>
  );
}

/** Collapsed file summary */
function CollapsedSummary({
  file,
  onExpand,
}: {
  file: FileDiffData;
  onExpand: () => void;
}) {
  const totalChanges = getTotalChangedLines(file);

  return (
    <div className="flex items-center justify-between px-4 py-3 text-sm text-content-secondary">
      <span>
        File has {totalChanges} changes (+{file.additions} / -{file.deletions}), click to expand
      </span>
      <button
        onClick={onExpand}
        className="rounded bg-accent-50 px-3 py-1 text-xs font-medium text-accent-700 hover:bg-accent-100 focus:outline-none focus:ring-2 focus:ring-brand-600 dark:bg-accent-950/30 dark:text-accent-300 dark:hover:bg-accent-950/50"
      >
        Expand
      </button>
    </div>
  );
}

/** Binary file message */
function BinaryFileMessage({ file }: { file: FileDiffData }) {
  return (
    <div className="flex items-center gap-2 px-4 py-3 text-sm text-content-secondary">
      <svg className="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
      <span>Binary file not shown</span>
      <span className="text-content-tertiary">({file.changeType.toLowerCase()})</span>
    </div>
  );
}

/** Single file diff section */
function FileDiffSection({
  file,
  viewMode,
  onGutterClick,
}: {
  file: FileDiffData;
  viewMode: 'unified' | 'split';
  onGutterClick?: (filePath: string, lineNumber: number, side: 'LEFT' | 'RIGHT') => void;
}) {
  const defaultCollapsed = shouldCollapseByDefault(file);
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  const parsedHunks = useMemo((): HunkData[] => {
    if (!file.diffText || isBinaryFile(file)) {
      return [];
    }

    try {
      const parsedFiles = parseDiff(file.diffText, { nearbySequences: 'zip' });
      if (parsedFiles.length > 0 && parsedFiles[0]) {
        return parsedFiles[0].hunks;
      }
    } catch {
      // If parsing fails, return empty hunks
    }
    return [];
  }, [file.diffText, file.isBinary]);

  const tokens = useTokenizedHunks(parsedHunks, file.filePath, viewMode);

  const handleGutterClick = useCallback(
    ({ change, side }: { change: { type: string; lineNumber?: number; oldLineNumber?: number; newLineNumber?: number } | null; side?: string }) => {
      if (!change || !onGutterClick) return;

      let lineNumber: number;
      let commentSide: 'LEFT' | 'RIGHT';

      if (change.type === 'delete') {
        lineNumber = change.lineNumber ?? change.oldLineNumber ?? 0;
        commentSide = 'LEFT';
      } else if (change.type === 'insert') {
        lineNumber = change.lineNumber ?? change.newLineNumber ?? 0;
        commentSide = 'RIGHT';
      } else {
        // Normal change - use the side to determine
        if (side === 'old') {
          lineNumber = change.oldLineNumber ?? 0;
          commentSide = 'LEFT';
        } else {
          lineNumber = change.newLineNumber ?? 0;
          commentSide = 'RIGHT';
        }
      }

      onGutterClick(file.filePath, lineNumber, commentSide);
    },
    [file.filePath, onGutterClick],
  );

  const gutterEvents = useMemo(() => {
    if (!onGutterClick) return {};
    return {
      onClick: handleGutterClick,
    };
  }, [onGutterClick, handleGutterClick]);

  if (isBinaryFile(file)) {
    return (
      <div
        className="overflow-hidden rounded-lg border border-border"
        data-testid={`diff-file-${file.filePath}`}
        id={`diff-file-${encodeURIComponent(file.filePath)}`}
      >
        <FileHeader
          file={file}
          isCollapsed={false}
          onToggleCollapse={() => {}}
        />
        <BinaryFileMessage file={file} />
      </div>
    );
  }

  return (
    <div
      className="overflow-hidden rounded-lg border border-border"
      data-testid={`diff-file-${file.filePath}`}
      id={`diff-file-${encodeURIComponent(file.filePath)}`}
    >
      <FileHeader
        file={file}
        isCollapsed={isCollapsed}
        onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
      />

      {isCollapsed ? (
        <CollapsedSummary file={file} onExpand={() => setIsCollapsed(false)} />
      ) : (
        <div className="diff-viewer-content overflow-x-auto">
          {parsedHunks.length > 0 ? (
            <Diff
              viewType={viewMode as ViewType}
              diffType={mapChangeType(file.changeType)}
              hunks={parsedHunks}
              tokens={tokens}
              gutterEvents={gutterEvents}
            >
              {(hunks: HunkData[]) =>
                hunks.map((hunk) => (
                  <Hunk key={`${hunk.content}-${hunk.oldStart}-${hunk.newStart}`} hunk={hunk} />
                ))
              }
            </Diff>
          ) : (
            <div className="px-4 py-3 text-sm text-content-tertiary">
              No diff content available
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * DiffViewer component that renders file diffs using react-diff-view.
 *
 * Features:
 * - Unified and split (side-by-side) view modes
 * - Line numbers on both sides
 * - Color-coded changes (green for additions, red for deletions, yellow for modifications)
 * - Collapses files with >500 changed lines by default
 * - Binary file detection with informational message
 * - Loading skeleton during data fetch
 * - Clickable gutter for inline comment placement
 */
export function DiffViewer({
  files,
  viewMode,
  isLoading = false,
  onGutterClick,
  onFileSelect: _onFileSelect,
  inlineCommentWidget,
}: DiffViewerProps) {
  if (isLoading) {
    return <DiffSkeleton />;
  }

  if (files.length === 0) {
    return (
      <div className="rounded-lg border border-border px-4 py-8 text-center text-sm text-content-tertiary">
        No file changes to display
      </div>
    );
  }

  return (
    <div className="diff-viewer space-y-4" role="region" aria-label="Code diff viewer">
      {files.map((file) => (
        <div key={file.filePath}>
          <FileDiffSection
            file={file}
            viewMode={viewMode}
            onGutterClick={onGutterClick}
          />
          {inlineCommentWidget && inlineCommentWidget.filePath === file.filePath && (
            <div className="mt-2 ml-4">{inlineCommentWidget.element}</div>
          )}
        </div>
      ))}
    </div>
  );
}
